/**
 * The write body of an HQ blog post: exactly the fields `BlogPostIn` accepts
 * (docs/hq-site-module/hq-blog-contract.md section 2). `cover_url`, `languages`,
 * `published_at`, `updated_at` and `author_email` are derived or server-owned on reads and
 * are never sent back.
 *
 * THE COVER. `cover_media_id` present in the body — an id, or null to clear — REPLACES
 * the post's cover; omitted, the cover is left as it is. A post written before the media
 * library shows a cover the server serves from its migrated legacy URL: `cover_url` set,
 * `cover_media_id` null. Until the operator picks or clears that cover, the field is
 * omitted, so saving the post (or toggling its publication) keeps the cover.
 */

import { isBlank, trimBody, trimEdges } from "../lib/blogBlank";

/** The three editable languages, in the server's reporting order. */
export const BLOG_LANGS = ["fr", "en", "es"];

/** The per-language text fields of a post. */
export const BLOG_LANG_FIELDS = ["title", "excerpt", "body", "tag"];

export function toBlogPayload(p) {
  const body = { slug: p.slug || "", published: Boolean(p.published) };
  for (const l of BLOG_LANGS) {
    for (const f of BLOG_LANG_FIELDS) body[`${f}_${l}`] = p[`${f}_${l}`] || "";
  }
  const untouchedLegacyCover = !p.cover_media_id && Boolean(p.cover_url);
  if (!untouchedLegacyCover) body.cover_media_id = p.cover_media_id || null;
  return body;
}

// The blank class and the trimming rules live in lib/blogBlank.js (the mirror of the
// server's `trim_edges` / `trim_body`); re-exported here for the editor's callers.
export { trimEdges, trimBody };

const filled = (v) => !isBlank(v);

/**
 * The languages with BOTH a title and a body (blank = whitespace only), in fr, en, es
 * order — the same definition the server uses for its derived `languages` field.
 */
export const completeLanguages = (p) =>
  BLOG_LANGS.filter((l) => filled(p[`title_${l}`]) && filled(p[`body_${l}`]));

/**
 * The languages with ANY non-blank field (title, excerpt, tag or body), in fr, en, es
 * order. A started language must be complete (contract section 1), so a post never mixes
 * the languages of its fields.
 */
export const startedLanguages = (p) =>
  BLOG_LANGS.filter((l) => BLOG_LANG_FIELDS.some((f) => filled(p[`${f}_${l}`])));

/**
 * Client mirror of the server's write rules, in the server's order: the first STARTED
 * language that is not complete, else "no complete language". `null` = valid.
 *
 * @returns {{code: "BLOG_LANGUAGE_INCOMPLETE", lang: string} | {code: "BLOG_NO_LANGUAGE"} | null}
 */
export function validateBlogForm(p) {
  const complete = completeLanguages(p);
  const unfinished = startedLanguages(p).find((l) => !complete.includes(l));
  if (unfinished) return { code: "BLOG_LANGUAGE_INCOMPLETE", lang: unfinished };
  return complete.length === 0 ? { code: "BLOG_NO_LANGUAGE" } : null;
}

/** A URL slug (`^[a-z0-9-]+$`) suggested from a title: accents folded, runs of other characters become one hyphen. */
export const slugFromTitle = (title) =>
  String(title || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
